import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Excalidraw,
  convertToExcalidrawElements,
} from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import {
  ArrowLeft,
  Loader2,
  Pause,
  Play,
  Settings,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { ApiError, lessonApi } from '@/lib/api-client';
import type { DoubtSceneCue } from '@/lib/api-client';

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

interface DoubtReplayProps {
  lessonId: string;
  sessionId: string;
  onExit?: () => void;
  /**
   * Optional controlled play/pause. When provided, `playing` drives
   * the audio element via effect, and `onPlayingChange` notifies the
   * parent of internal play/pause/end events. Used so the doubts
   * list card can also act as a play/pause control without lifting
   * the audio element itself.
   */
  playing?: boolean;
  onPlayingChange?: (playing: boolean) => void;
}

interface ResolvedCue extends DoubtSceneCue {
  /** Pre-fetched Excalidraw element list, ready for `convertToExcalidrawElements`. */
  elements: unknown[] | null;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function DoubtReplay({
  lessonId,
  sessionId,
  onExit,
  playing,
  onPlayingChange,
}: DoubtReplayProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [cues, setCues] = useState<ResolvedCue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  // Auto-hide the controls bar after a couple of seconds of mouse
  // inactivity, mirroring the main video player's behavior. Keep it
  // visible while paused so the user has obvious affordance to resume.
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Load the stitched audio URL + scene timeline. Pre-fetches every
  // scene JSON so swaps during playback are state-only (no network).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIsPlaying(false);
    setCurrentMs(0);
    setCues([]);
    setAudioUrl(null);
    setDurationMs(0);

    // Poll once every 1.5 s while the backend reports 202
    // (`finalize_doubt_session` still stitching). Most sessions
    // finalize in 2-5 s, so a few attempts is plenty. Cap at 8
    // attempts (~12 s) before showing an error.
    const MAX_POLL_ATTEMPTS = 8;
    const POLL_INTERVAL_MS = 1500;

    async function load(attempt = 0) {
      try {
        const recording = await lessonApi.getDoubtSessionRecording(
          lessonId,
          sessionId
        );
        if (cancelled) return;
        setAudioUrl(recording.audio_url);
        setDurationMs(recording.duration_ms);

        const resolved = await Promise.all(
          recording.scenes.map(async cue => {
            try {
              const res = await fetch(cue.scene_url);
              const json = await res.json();
              return {
                ...cue,
                elements: (json?.elements ?? json ?? null) as unknown[] | null,
              };
            } catch {
              return { ...cue, elements: null };
            }
          })
        );
        if (cancelled) return;
        setCues(resolved.sort((a, b) => a.appears_at_ms - b.appears_at_ms));
        setLoading(false);
      } catch (err) {
        // 202 = backend says "still processing". Keep the loader on
        // and try again shortly - the frontend used to fall through
        // to a generic error here, which made a freshly-finished
        // session look broken for a few seconds before stitching
        // wrapped up.
        if (
          err instanceof ApiError &&
          err.status === 202 &&
          attempt < MAX_POLL_ATTEMPTS
        ) {
          if (cancelled) return;
          setTimeout(() => {
            if (!cancelled) load(attempt + 1);
          }, POLL_INTERVAL_MS);
          return;
        }
        if (!cancelled) {
          setError(
            err instanceof ApiError && err.status === 202
              ? 'This session is still being prepared. Please retry shortly.'
              : 'Could not load this session.'
          );
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [lessonId, sessionId]);

  // Auto-play once BOTH the audio URL and the scene timeline are
  // ready. Without this gate, audio would start playing while
  // `Promise.all` was still resolving scene JSONs, producing 1-2 s
  // of blank canvas at the start of replay before the first cue
  // landed. Browser autoplay policy may still block; the user can
  // press play if so.
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;
    if (cues.length === 0) return; // wait for scenes
    if (playing === false) return;
    audioRef.current.play().catch(() => {});
  }, [audioUrl, cues, playing]);

  // Honor the controlled `playing` prop. When the parent toggles it
  // (e.g. via the doubt list card), drive the audio element to match.
  // The effect is keyed on `playing` only - internal state changes
  // (audio onPlay/onPause) flow up via onPlayingChange instead.
  useEffect(() => {
    if (playing === undefined) return;
    const a = audioRef.current;
    if (!a) return;
    if (playing && a.paused) a.play().catch(() => {});
    else if (!playing && !a.paused) a.pause();
  }, [playing]);

  // Active scene = most-recent cue whose `appears_at_ms <= currentMs`.
  // Walking from the end is faster than scanning forward.
  const activeCueIndex = useMemo(() => {
    for (let i = cues.length - 1; i >= 0; i--) {
      if (cues[i].appears_at_ms <= currentMs) return i;
    }
    return -1;
  }, [cues, currentMs]);

  const activeElements = useMemo(() => {
    const els = activeCueIndex >= 0 ? cues[activeCueIndex].elements : null;
    return els ? convertToExcalidrawElements(els as never) : [];
  }, [activeCueIndex, cues]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play();
    else a.pause();
  };

  // Seek by ms offset relative to current playhead. Used by both the
  // skip-back/-forward buttons and (negated) by the scrubber click.
  const seekToMs = useCallback(
    (ms: number) => {
      const a = audioRef.current;
      if (!a || !durationMs) return;
      const clamped = Math.max(0, Math.min(durationMs, ms));
      a.currentTime = clamped / 1000;
      setCurrentMs(clamped);
    },
    [durationMs]
  );

  const handleSpeedChange = (rate: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
  };

  // Auto-hide control bar after 2.5s mouse-idle, mirror video player.
  // Always visible while paused so the user can resume; visible while
  // the speed menu is open so it doesn't disappear under the user's
  // cursor.
  const showControlsAndStartTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    if (isPlaying && !showSpeedMenu) {
      hideControlsTimerRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2500);
    }
  }, [isPlaying, showSpeedMenu]);

  useEffect(() => {
    showControlsAndStartTimer();
    return () => {
      if (hideControlsTimerRef.current) {
        clearTimeout(hideControlsTimerRef.current);
      }
    };
  }, [showControlsAndStartTimer]);

  // Hold an imperative handle to the Excalidraw API so we can call
  // `updateScene` between cues instead of force-remounting via `key`.
  // Remounting reset Excalidraw's internal state (zoom/pan/canvas
  // sizing) on every transition and produced a visible flicker.
  const excalidrawAPIRef = useRef<{
    updateScene: (data: unknown) => void;
  } | null>(null);

  // Push the active scene's elements to Excalidraw whenever the
  // active cue changes. The first scene populates the canvas via
  // `initialData` (set when Excalidraw mounts), every subsequent
  // change is an in-place update.
  useEffect(() => {
    const api = excalidrawAPIRef.current;
    if (!api) return;
    api.updateScene({ elements: activeElements });
  }, [activeElements]);

  // Track-position from a click anywhere on the progress bar.
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!durationMs) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekToMs(pos * durationMs);
  };

  const progress = durationMs ? (currentMs / durationMs) * 100 : 0;

  if (loading) {
    return (
      <div className='flex h-full w-full items-center justify-center bg-card'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error || !audioUrl) {
    return (
      <div className='flex h-full w-full flex-col items-center justify-center gap-3 bg-card'>
        <p className='text-sm text-red-600 dark:text-red-400'>
          {error ?? 'Recording not available.'}
        </p>
        {onExit && (
          <button
            type='button'
            onClick={onExit}
            className='rounded-md border border-border px-3 py-1.5 text-sm'
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onMouseMove={showControlsAndStartTimer}
      onMouseLeave={() => {
        if (isPlaying && !showSpeedMenu) setShowControls(false);
      }}
      className='relative flex h-full w-full flex-col bg-card'
    >
      {/* "Back to video" - hidden when controls auto-hide so it doesn't
          float over the canvas during quiet playback. */}
      {onExit && (
        <button
          type='button'
          onClick={onExit}
          aria-label='Back to video'
          className={`absolute left-3 top-3 z-30 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-sm font-medium text-white shadow-md transition-opacity duration-300 hover:bg-black/80 ${
            showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <ArrowLeft className='h-4 w-4' />
          <span>Back to video</span>
        </button>
      )}

      <div className='doubt-canvas-host flex-1 overflow-hidden'>
        <Excalidraw
          excalidrawAPI={(api: unknown) => {
            excalidrawAPIRef.current = api as typeof excalidrawAPIRef.current;
          }}
          initialData={{
            elements: activeElements,
            appState: {
              viewBackgroundColor: '#ffffff',
              zenModeEnabled: true,
            },
          }}
          viewModeEnabled={true}
          zenModeEnabled={true}
          gridModeEnabled={false}
          theme='light'
        />
      </div>

      {/* Control bar mirrors the main video player (VideoRenderer): a
          bottom gradient overlay that auto-fades on mouse idle, with a
          progress bar above two rows of icon buttons. We deliberately
          omit captions / Ask-Doubt / fullscreen - none apply to a
          recorded doubt session. */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/85 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onMouseMove={showControlsAndStartTimer}
      >
        <div className='space-y-2'>
          {/* Progress bar with elapsed / total. Click anywhere to seek. */}
          <div className='flex items-center gap-2'>
            <span className='min-w-[40px] font-mono text-xs tabular-nums text-white/75'>
              {formatTime(currentMs / 1000)}
            </span>
            <div
              className='h-1 flex-1 cursor-pointer overflow-hidden rounded-full bg-white/30'
              onClick={handleProgressClick}
            >
              <div
                className='h-full bg-blue-500 transition-all duration-100 dark:bg-primary'
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className='min-w-[40px] font-mono text-xs tabular-nums text-white/75'>
              {formatTime(durationMs / 1000)}
            </span>
          </div>

          {/* Buttons row */}
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-1'>
              <button
                type='button'
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                className='rounded-md p-2 text-white transition-colors hover:bg-white/20'
              >
                {isPlaying ? (
                  <Pause className='h-4 w-4' />
                ) : (
                  <Play className='h-4 w-4' />
                )}
              </button>
              <button
                type='button'
                onClick={() => seekToMs(currentMs - 10000)}
                aria-label='Skip back 10 seconds'
                className='rounded-md p-2 text-white transition-colors hover:bg-white/20'
              >
                <SkipBack className='h-4 w-4' />
              </button>
              <button
                type='button'
                onClick={() => seekToMs(currentMs + 10000)}
                aria-label='Skip forward 10 seconds'
                className='rounded-md p-2 text-white transition-colors hover:bg-white/20'
              >
                <SkipForward className='h-4 w-4' />
              </button>
            </div>

            <div className='flex items-center gap-1'>
              {/* Speed menu - same pattern as VideoRenderer's settings
                  popover. We omit captions/fullscreen/volume since the
                  recording is single-track audio + a static canvas. */}
              <div className='relative'>
                <button
                  type='button'
                  onClick={() => setShowSpeedMenu(s => !s)}
                  aria-label='Playback speed'
                  className={`flex items-center gap-1 rounded-md px-2 py-2 text-xs font-medium text-white transition-colors hover:bg-white/20 ${
                    showSpeedMenu ? 'bg-white/10' : ''
                  }`}
                >
                  <Settings className='h-4 w-4' />
                  <span className='font-mono tabular-nums'>
                    {playbackRate}×
                  </span>
                </button>
                {showSpeedMenu && (
                  <div className='absolute bottom-full right-0 mb-2 min-w-[80px] rounded-lg bg-black/90 p-2 shadow-lg'>
                    {PLAYBACK_RATES.map(rate => (
                      <button
                        key={rate}
                        onClick={() => handleSpeedChange(rate)}
                        className={`block w-full rounded-sm px-2 py-1 text-left text-sm text-white hover:bg-white/20 ${
                          playbackRate === rate ? 'bg-blue-600' : ''
                        }`}
                      >
                        {rate}×
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        preload='metadata'
        onTimeUpdate={() => {
          const a = audioRef.current;
          if (a) setCurrentMs(Math.round(a.currentTime * 1000));
        }}
        onPlay={() => {
          setIsPlaying(true);
          onPlayingChange?.(true);
        }}
        onPause={() => {
          setIsPlaying(false);
          onPlayingChange?.(false);
        }}
        onEnded={() => {
          setIsPlaying(false);
          onPlayingChange?.(false);
        }}
        // If the audio element errors (most likely cause: presigned
        // URL expired while the user had the tab paused), re-fetch
        // the recording endpoint to get a fresh URL and resume from
        // the current playhead.
        onError={async () => {
          try {
            const recording = await lessonApi.getDoubtSessionRecording(
              lessonId,
              sessionId
            );
            const a = audioRef.current;
            if (!a) return;
            const resumeAt = currentMs / 1000;
            setAudioUrl(recording.audio_url);
            setDurationMs(recording.duration_ms);
            // Wait a tick so React applies the new src, then seek +
            // resume if we were playing.
            requestAnimationFrame(() => {
              const cur = audioRef.current;
              if (!cur) return;
              cur.currentTime = resumeAt;
              if (isPlaying) cur.play().catch(() => {});
            });
          } catch {
            setError('Audio playback failed. Please reopen this session.');
          }
        }}
        className='hidden'
      />
    </div>
  );
}
